import { useEffect, useRef } from 'react';
import useScheduleStore from '../stores/useScheduleStore';
import { ScheduleEvent } from './types';

interface DragControllerProps {
  eventLookup: Map<string, ScheduleEvent>;
  /** All shifts (real, from DB) — used to detect cross-event overlap during preview. */
  shifts: { user_id: string; event_id: string; start_date: string; end_date: string }[];
}

/**
 * Mounted once at the page level. While a drag is active, attaches global
 * pointermove + pointerup + keyup listeners; uses elementFromPoint to find
 * the cell under the pointer and dispatches updateDrag. On pointerup,
 * commits via the store (which emits dated pending changes).
 *
 * Renders nothing.
 *
 * Implementation note: `shifts` and `eventLookup` are read via refs inside
 * the handlers, NOT via the effect deps. This keeps the listener
 * attach/detach scoped to `!!drag` transitions (twice per drag). Without
 * the refs, any parent re-render that produces a new prop reference
 * (e.g. pendingChange tick → effectiveDayBars recompute → new eventLookup
 * reference) would tear down and re-add all three window listeners.
 */
const DragController: React.FC<DragControllerProps> = ({ eventLookup, shifts }) => {
  const dragActive = useScheduleStore(s => !!s.drag);
  const updateDrag = useScheduleStore(s => s.updateDrag);
  const commitDrag = useScheduleStore(s => s.commitDrag);
  const cancelDrag = useScheduleStore(s => s.cancelDrag);

  const eventLookupRef = useRef(eventLookup);
  const shiftsRef = useRef(shifts);
  eventLookupRef.current = eventLookup;
  shiftsRef.current = shifts;

  useEffect(() => {
    if (!dragActive) return;

    const onMove = (e: PointerEvent) => {
      const drag = useScheduleStore.getState().drag;
      if (!drag) return;

      const target = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      if (!target) return;
      const cell = target.closest('[data-date][data-user-id]') as HTMLElement | null;
      if (!cell) return;
      const date = cell.dataset.date!;
      const userId = cell.dataset.userId!;

      // Drag stays in the originating user's row
      if (userId !== drag.userId) return;

      let invalid = false;

      if (drag.kind === 'block') {
        // Block drags don't belong to an event range. They DO invalidate if
        // the user is already shifted on the target day, because the backend
        // createBlock rejects with 409 when shifts overlap the block range.
        // Live feedback prevents a surprising save-time error.
        const shiftHere = shiftsRef.current.find(
          s => s.user_id === drag.userId && s.start_date <= date && s.end_date >= date,
        );
        if (shiftHere) invalid = true;
      } else {
        // Shift drag: must stay within the event range AND not collide with
        // another event for this user.
        const event = eventLookupRef.current.get(drag.eventId);
        if (event) {
          const evtStart = event.event_date;
          const evtEnd = event.end_date || event.event_date;
          if (date < evtStart || date > evtEnd) invalid = true;
        }
        if (!invalid) {
          const conflict = shiftsRef.current.find(
            s =>
              s.user_id === drag.userId &&
              s.event_id !== drag.eventId &&
              s.start_date <= date &&
              s.end_date >= date,
          );
          if (conflict) invalid = true;
        }
      }

      updateDrag(date, invalid);
    };

    const onUp = (e: PointerEvent) => {
      // If pointer-up landed outside any cell (off-grid, off-window), CANCEL
      // rather than commit the last valid preview.
      const target = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      const cell = target?.closest('[data-date][data-user-id]') ?? null;
      if (!cell) {
        cancelDrag();
      } else {
        commitDrag();
      }
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancelDrag();
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('keydown', onKey);
    };
  }, [dragActive, updateDrag, commitDrag, cancelDrag]);

  return null;
};

export default DragController;
